"use client";

import { ANALYTICS_EVENTS, track } from "@/lib/analyticsEvents";
import { AccessibleAudioPlayer } from "@/components/audio/AccessibleAudioPlayer";
import { api } from "convex/_generated/api";
import { useAction } from "convex/react";
import { Headphones, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { useCallback, useState } from "react";

let cachedExample: string | null = null;
let exampleRequest: Promise<string> | null = null;

export function AudioExamplePlayer({ onTrySample }: { onTrySample: () => void }) {
  const previewExample = useAction(api.actions.previewAudioVoice.previewExample);
  const [src, setSrc] = useState<string | null>(cachedExample);
  const [state, setState] = useState<"idle" | "loading" | "ready" | "error">(
    cachedExample ? "ready" : "idle",
  );

  const load = useCallback(async () => {
    if (src) return;
    setState("loading");
    try {
      if (!exampleRequest) {
        exampleRequest = previewExample({}).then((result) => result.audioDataUrl);
      }
      const next = await exampleRequest;
      cachedExample = next;
      setSrc(next);
      setState("ready");
    } catch {
      exampleRequest = null;
      setState("error");
    }
  }, [previewExample, src]);

  return (
    <section aria-labelledby="audio-example-heading" className="audio-example-row rounded-xl border border-border bg-card p-4 shadow-sm sm:p-5">
      <div className="flex flex-col gap-4">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-success/10 text-success" aria-hidden>
            <Headphones className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 id="audio-example-heading" className="text-sm font-semibold text-foreground">Hear an example</h2>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Teacher and student · two distinct voices · American English · about 12 seconds</p>
          </div>
        </div>

        <div className="min-w-0 flex-[1.15]">
          {src ? (
            <AccessibleAudioPlayer
              src={src}
              title="Darsi two-speaker classroom example"
              compact
              onFirstPlay={() => track(ANALYTICS_EVENTS.audioSamplePlayed, { speaker_count: 2, locale: "en-US" })}
            />
          ) : (
            <button
              type="button"
              onClick={() => void load()}
              disabled={state === "loading"}
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-foreground bg-foreground px-4 text-sm font-semibold text-background hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60"
            >
              {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" /> : state === "error" ? <RefreshCw className="h-4 w-4" /> : <Headphones className="h-4 w-4" />}
              {state === "loading" ? "Preparing example…" : state === "error" ? "Try example again" : "Prepare voice example"}
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={onTrySample}
          className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-border bg-card px-4 text-sm font-semibold text-foreground hover:border-success/40 hover:bg-success/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success"
        >
          <Sparkles className="h-4 w-4 text-success" />
          Try this sample
        </button>
      </div>
      {state === "error" ? <p role="alert" className="mt-3 text-xs text-destructive">The voice provider could not prepare the example. Your content has not been changed.</p> : null}
    </section>
  );
}
